// Collapsible breakdown of what makes up the running total. Mirrors the
// math in `pricing.ts` exactly so customers can verify per-spot fees +
// digitisation (charged once per unique logo) + per-spot additional fee
// + the optional approval fee.
//
// Pure <details>/<summary>: no React state. Browser handles open/close.

import type { Config } from "../types";
import { flowKindFor } from "../flow";
import {
  additionalPlacementFeeFor,
  approvalFeeFor,
  digitisationEventsFor,
  digitisationOriginalFeeFor,
  effectiveTiersFor,
  effectiveUpsellPerCountCents,
  effectiveUpsellPriceCents,
  effectiveUpsellStyleSurchargeCents,
  isDigitisationFreePromo,
  matchDesignSurchargeCentsFor,
  tierForSize,
  type ApprovalChoice,
  type Spot,
} from "../pricing";

export function PriceBreakdown({
  spots,
  config,
  approvalChoice,
  approvalAddonId,
  orderAddOnIds,
  garmentPriceCents,
}: {
  spots: Spot[];
  config: Config;
  approvalChoice: ApprovalChoice;
  /** Phase 22b: approval-step replacement upsell when one's picked. */
  approvalAddonId: string | null;
  /** Phase 37: order-level upsell IDs the customer ticked. */
  orderAddOnIds: string[];
  /** Phase B6: selected garment variant price (cents). Shown as the
   *  first line so the breakdown explains the full item total. */
  garmentPriceCents?: number | null;
}) {
  // Phase 68: `wasCents` carries the original (pre-promo) price for
  // strikethrough display. When set, the row renders "~~$30~~ Free"
  // instead of just "Free", so the customer sees the value of the
  // promo applied. Only set on digitisation / pet+kid scan rows when
  // the free-digitisation promo is on.
  const lines: { label: string; cents: number; plain?: boolean; wasCents?: number }[] = [];
  if (typeof garmentPriceCents === "number" && garmentPriceCents > 0) {
    lines.push({ label: "Garment", cents: garmentPriceCents, plain: true });
  }
  const seenLogos = new Set<string>();
  const promo = isDigitisationFreePromo(config);
  // Phase 85 / audit 2026-10: the digitisation rows come straight from
  // pricing.digitisationEventsFor - the single source of truth the bar
  // total, the per-spot subtotal and the cart-line breakdown all use - so
  // this list can never miss a charge the total includes. It used to
  // re-derive them from uploadStagingKey, which dropped the "I'll send it
  // later" spot (no staging key yet) even though the total charged it
  // $30. `waived` marks the free first design (or every event under the
  // blanket promo); `cents` is the pre-waiver cost.
  const digiEvents = digitisationEventsFor(spots, config);
  const digiWaivedSpots = new Set(
    digiEvents.filter((e) => e.waived).map((e) => e.spot),
  );

  spots.forEach((s, idx) => {
    const p = config.placements.find((pp) => pp.slug === s.placementSlug);
    if (!p) return;
    const tiers = effectiveTiersFor(p, config);
    const tierFee = tierForSize(s.sizeMm, tiers, tiers.length > 0).feeCents;
    lines.push({
      label: `Spot ${idx + 1} · ${p.name} · ${s.sizeMm}mm`,
      cents: tierFee,
    });

    // Standard (logo-flow) digitisation row, driven by this spot's event.
    // digitisationEventsFor already dedupes one artwork across spots (a
    // second spot stitching the same file gets no event, so no row),
    // skips already-digitised files and scan-upsell spots (the scan IS
    // the digitisation, rendered below), and charges the upload-later
    // spot like any other new design - so every rule the total applies
    // shows up here with the same cents.
    const logoEvent = digiEvents.find(
      (e) => e.spot === s && e.kind === "logo",
    );
    if (logoEvent) {
      const detail = s.uploadFilename
        ? ` (${s.uploadFilename})`
        : !s.uploadStagingKey && s.uploadLater
          ? " (sending it later)"
          : "";
      lines.push({
        label: `↳ Design digitisation${detail}`,
        cents: logoEvent.waived ? 0 : logoEvent.cents,
        // Show the original $30 crossed out next to "Free" when waived
        // (first design, or the blanket promo).
        wasCents: logoEvent.waived
          ? digitisationOriginalFeeFor(config)
          : undefined,
      });
    } else if (
      (s.mode === "logo" || s.mode === "both") &&
      s.uploadStagingKey &&
      s.uploadAlreadyDigitised &&
      !s.addonUpsellId &&
      !seenLogos.has(s.uploadStagingKey)
    ) {
      // No event (nothing to charge): surface the waiver as a $0 line so
      // the customer sees the value. Deduped per file like the events.
      seenLogos.add(s.uploadStagingKey);
      lines.push({
        label: `↳ Design digitisation - already paid`,
        cents: 0,
      });
    }

    // Additional-placement fee. First spot is free; from 2nd onward, the
    // configured ladder applies.
    const nth = idx + 1;
    const extraFee = additionalPlacementFeeFor(config, nth);
    if (extraFee > 0) {
      const ladderEntry = config.product.additionalPlacementFees?.find(
        (f) => f.nth === nth,
      );
      const label = ladderEntry?.label
        ? `↳ ${ladderEntry.label}`
        : `↳ Additional placement fee`;
      lines.push({ label, cents: extraFee });
    }

    // Phase 17c: replaces-step add-on (pet portrait, kid drawing, etc.).
    // The scan upsell IS the digitisation — waived for a reused design
    // that was already digitised + paid on the original.
    if (s.addonUpsellId) {
      const upsell = config.upsells?.find((u) => u.id === s.addonUpsellId);
      if (upsell && s.uploadAlreadyDigitised) {
        lines.push({
          label: `↳ ${upsell.label} — already digitised`,
          cents: 0,
        });
        // RK24263 (2026-07-18): coloured + filled is a per-garment stitch
        // upgrade, not digitisation - a reused design still pays it, so
        // the breakdown must show it or checkout charges more than the
        // customer saw.
        const reusedStyleSurcharge = effectiveUpsellStyleSurchargeCents(
          upsell,
          config,
        );
        if (reusedStyleSurcharge && s.addonStyleColoured) {
          lines.push({
            label: `   ↳ Coloured + filled`,
            cents: reusedStyleSurcharge,
          });
        }
      } else if (upsell) {
        // Phase 68: pet/kid scan upsell IS the digitisation step on
        // those flows, so the promo zeroes its price + its per-count
        // and style-upgrade surcharges. wasCents shows the customer
        // the original prices crossed out.
        const promoCoversThisUpsell = promo && upsell.replacesStep === "logo-upload";
        // Phase 85: the scan base IS this spot's digitisation, so the
        // first design's scan is free (included in the garment price);
        // additional pet/kid designs pay it. Per-count + style below
        // are NOT digitisation and always charge.
        const scanWaived = digiWaivedSpots.has(s);
        lines.push({
          label: `↳ ${upsell.label}`,
          cents: scanWaived ? 0 : effectiveUpsellPriceCents(upsell, config),
          wasCents:
            scanWaived || promoCoversThisUpsell ? upsell.priceCents : undefined,
        });
        // Phase 33b + 39c: per-pet extras driven by either the upload
        // count (pet flow) or the legacy stepper value.
        const effectiveCount =
          (s.extraUploads?.length ?? 0) > 0
            ? 1 + (s.extraUploads?.length ?? 0)
            : s.addonCount ?? 1;
        const perCount = effectiveUpsellPerCountCents(upsell, config);
        if (upsell.countMax && (perCount || (promoCoversThisUpsell && upsell.perCountCents)) && effectiveCount > 1) {
          const extras = effectiveCount - 1;
          // Phase 68b / 70: noun matches the flow ("extra pet" vs
          // "extra person"; kid flow doesn't use multi-image). Source
          // the kind from the FlowBlueprint when bound.
          const flow = flowKindFor(config);
          const subjectOne = flow === "person" ? "person" : "pet";
          const subjectMany = flow === "person" ? "people" : "pets";
          lines.push({
            label: `   ↳ ${extras} extra ${extras === 1 ? subjectOne : subjectMany}`,
            cents: extras * perCount,
            wasCents: promoCoversThisUpsell && upsell.perCountCents ? extras * upsell.perCountCents : undefined,
          });
        }
        // Phase 33c: filled-style upgrade.
        // Phase 68: covered by the free-digitisation promo (it's
        // bundled into the same scan-upsell pricing). When the promo
        // zeroes styleSurcharge, still render the row with the
        // original price crossed out so the customer sees the saving.
        const styleSurcharge = effectiveUpsellStyleSurchargeCents(upsell, config);
        const rawStyleSurcharge = upsell.styleSurchargeCents ?? 0;
        if ((styleSurcharge || (promoCoversThisUpsell && rawStyleSurcharge)) && s.addonStyleColoured) {
          lines.push({
            label: `   ↳ Coloured + filled`,
            cents: styleSurcharge,
            wasCents: promoCoversThisUpsell && rawStyleSurcharge ? rawStyleSurcharge : undefined,
          });
        }
      }
    }
    // Phase 33d: stackable extras (sleeve emojis).
    if (s.extraAddonIds && s.extraAddonIds.length > 0) {
      for (const id of s.extraAddonIds) {
        const x = config.upsells?.find((u) => u.id === id);
        if (x) lines.push({ label: `↳ ${x.label}`, cents: x.priceCents });
      }
    }
    // 2026-08-14: "Match the design" threads carry the coloured + filled
    // fee when no add-on style surcharge already covers this spot. Must
    // mirror totalCentsFor or the breakdown stops summing to the bar.
    const matchCents = matchDesignSurchargeCentsFor(s, config);
    if (matchCents > 0) {
      lines.push({
        label: `↳ Coloured + filled (matched threads)`,
        cents: matchCents,
      });
    }
  });

  if (approvalChoice === "approve") {
    // Phase 22b: when an approval-replacing upsell is picked, use its
    // label + price instead of the standard fee.
    const addon = approvalAddonId
      ? config.upsells?.find((u) => u.id === approvalAddonId)
      : null;
    if (addon) {
      lines.push({ label: addon.label, cents: addon.priceCents });
    } else {
      lines.push({ label: "Design approval", cents: approvalFeeFor(config) });
    }
  }

  // Phase 37: order-level add-ons (Care kit, Gift box, Rush production).
  for (const id of orderAddOnIds) {
    const u = config.upsells?.find((x) => x.id === id);
    if (u) lines.push({ label: u.label, cents: u.priceCents });
  }

  if (lines.length === 0) return null;

  const money = (c: number) => {
    const d = c / 100;
    return Number.isInteger(d) ? `$${d}` : `$${d.toFixed(2)}`;
  };
  const fmt = (l: { cents: number; plain?: boolean }) =>
    l.plain ? money(l.cents) : l.cents === 0 ? "Free" : `+${money(l.cents)}`;

  return (
    <details className="rk-pc-breakdown">
      <summary className="rk-pc-breakdown__summary">
        <span className="rk-pc-breakdown__label">What's in this price?</span>
        <span className="rk-pc-breakdown__chev" aria-hidden="true">▾</span>
      </summary>
      <ul className="rk-pc-breakdown__list">
        {lines.map((l, i) => (
          <li key={i} className="rk-pc-breakdown__row">
            <span className="rk-pc-breakdown__row-label">{l.label}</span>
            <span className="rk-pc-breakdown__row-cents">
              {/* Phase 68: when the promo waived a previously-paid fee,
                  show the original price crossed out alongside Free /
                  the new value, so the customer sees the saving. */}
              {typeof l.wasCents === "number" && l.wasCents > 0 && (
                <span className="rk-pc-breakdown__was">{money(l.wasCents)}</span>
              )}
              <span className={typeof l.wasCents === "number" && l.wasCents > 0 ? "rk-pc-breakdown__free" : undefined}>
                {fmt(l)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
